/*
PROV: COHINT030.BF.GA4_LOADER_GATE
REQ: cohorta-group-admin#30
WHY: GA4 gtag loader gated on cookie consent (ADR-007 loader-gate pattern).
     gtag is injected here, at runtime, only after the gate passes — never
     statically from _document.jsx — so nothing fires pre-consent. This issue
     ships plumbing ONLY: auto page-view tracking with a `surface` parameter,
     no custom event allowlist (a future Engagement Epic adds events). See
     docs/analytics-events.md. Named gaAnalytics to avoid colliding with the
     pre-existing src/lib/analytics queue on the sibling support surface;
     kept identical across both operator repos.
*/

import { logger } from "./logger";
import { hasAnalyticsConsent } from "./consent";

/**
 * Surface parameter attached to every event (shared GA4 property convention,
 * settled in cohorta-website#14): `marketing` | `group_admin` | `support`.
 */
export const SURFACE = "group_admin";

declare global {
  interface Window {
    __COHORTA_RUNTIME__?: Record<string, string>;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Resolve a NEXT_PUBLIC_* var at runtime from the window.__COHORTA_RUNTIME__
 * channel injected by _document.jsx (build-once / deploy-many on Cloud Run),
 * falling back to the build-time value for local dev and tests. Mirrors the
 * runtimeVar pattern in @cohorta/shared/auth/firebase.
 */
function runtimeVar(name: string, buildTimeFallback: string): string {
  if (typeof window !== "undefined") {
    const val = window.__COHORTA_RUNTIME__?.[name];
    if (val) return val;
  }
  return buildTimeFallback;
}

function measurementId(): string {
  return runtimeVar("NEXT_PUBLIC_GA4_MEASUREMENT_ID", process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID || "");
}

function analyticsEnabled(): boolean {
  return (
    runtimeVar("NEXT_PUBLIC_ANALYTICS_ENABLED", process.env.NEXT_PUBLIC_ANALYTICS_ENABLED || "") === "true"
  );
}

/** Configured = env flag on AND a measurement ID is present (fails closed otherwise). */
function isConfigured(): boolean {
  return analyticsEnabled() && measurementId() !== "";
}

let gtagLoaded = false;

function setGaDisabled(disabled: boolean): void {
  // Official GA4 opt-out flag: when true, a loaded gtag library sends nothing.
  const id = measurementId();
  if (!id) return;
  (window as unknown as Record<string, unknown>)[`ga-disable-${id}`] = disabled;
}

/**
 * Inject the gtag loader. Idempotent. Call only after the gate passes —
 * this is the single point where the GA script tag enters the page.
 */
function loadGtag(): void {
  if (gtagLoaded || typeof document === "undefined") return;
  const id = measurementId();
  if (!id) return;
  gtagLoaded = true;

  setGaDisabled(false);

  window.dataLayer = window.dataLayer || [];
  // The official snippet pushes the Arguments object, not an array — the
  // gtag library distinguishes the two, so this must not be a rest-array.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };

  window.gtag("js", new Date());
  // `surface` here is a config-level parameter: GA4 attaches it to every
  // event from this stream, including the automatic page_view.
  window.gtag("config", id, {
    send_page_view: true,
    surface: SURFACE,
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);

  logger.info("analytics_gtag_loaded", { measurement_id: id, surface: SURFACE });
}

/**
 * Evaluate the ADR-007 loader gate: env flag + measurement ID AND analytics
 * consent. Safe to call repeatedly — on first grant it injects gtag; on
 * revocation it flips the ga-disable flag (the script cannot be unloaded
 * mid-session); on re-grant it flips the flag back. While #439's consent
 * banner has not shipped, no consent record exists, so this stays dormant.
 */
export function evaluateAnalyticsGate(): void {
  if (typeof window === "undefined" || !isConfigured()) return;

  if (hasAnalyticsConsent()) {
    if (gtagLoaded) {
      setGaDisabled(false);
    } else {
      loadGtag();
    }
  } else {
    setGaDisabled(true);
  }
}

/** Emit page_view for client-side route changes (initial load is automatic). */
export function trackPageView(path: string): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}
